/* Alternix cart cross-sells: Shopify Search & Discovery; no external dependencies. */
(() => {
  if (window.__alternixCrossSellLoaded) return;
  window.__alternixCrossSellLoaded = true;

  const root = (window.Shopify?.routes?.root || '/');
  const endpoint = `${root}recommendations/products.json`;
  const cache = new Map();
  const esc = value => String(value ?? '').replace(/[&<>"']/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));
  const currency = cents => new Intl.NumberFormat('en-GB', {style:'currency',currency:'GBP'}).format(cents / 100);
  let generation = 0;
  let updating = false;

  async function getJSON(url, options) {
    const response = await fetch(url, {credentials:'same-origin', ...options});
    if (!response.ok) throw new Error(`Cart recommendations: ${response.status}`);
    return response.json();
  }

  function recommendations(productId, intent) {
    const key = `${productId}:${intent}`;
    if (!cache.has(key)) {
      const url = `${endpoint}?product_id=${encodeURIComponent(productId)}&limit=10&intent=${intent}`;
      cache.set(key, getJSON(url).then(data => data.products || []).catch(() => []));
    }
    return cache.get(key);
  }

  // Shopify's recommendations are product-based. Try a maximum of 3 basket seeds.
  async function candidates(cart) {
    const items = cart.items || [];
    const inBasket = new Set(items.map(item => Number(item.product_id)));
    const basketHandles = new Set(items.map(item => item.handle).filter(Boolean));
    const seedIds = [...new Set(items.map(item => Number(item.product_id)))].filter(Boolean).slice(0, 3);
    const seen = new Set(inBasket);
    const picked = [];
    const take = products => {
      for (const product of products) {
        const variant = product.variants?.find(v => v.available);
        if (!product.available || !variant || seen.has(Number(product.id)) || basketHandles.has(product.handle)) continue;
        seen.add(Number(product.id));
        picked.push({...product, chosenVariant: variant});
        if (picked.length >= 6) break;
      }
    };
    const complementary = await Promise.all(seedIds.map(id => recommendations(id, 'complementary')));
    complementary.forEach(take);
    if (picked.length < 4) {
      const related = await Promise.all(seedIds.slice(0, 2).map(id => recommendations(id, 'related')));
      related.forEach(take);
    }
    return picked.slice(0, 6);
  }

  function card(product) {
    const variant = product.chosenVariant;
    const hasOptions = product.variants?.length > 1;
    const image = product.featured_image?.url || product.featured_image || product.images?.[0] || '';
    const imgUrl = typeof image === 'string' ? image : '';
    const img = imgUrl ? `<img loading="lazy" width="100" height="100" src="${esc(imgUrl)}" alt="${esc(product.title)}">` : '';
    return `<article class="alternix-cross-sell__card">
      <a href="${esc(product.url)}" class="alternix-cross-sell__image">${img}</a>
      <div class="alternix-cross-sell__info">
        <a href="${esc(product.url)}" class="alternix-cross-sell__name">${esc(product.title)}</a>
        <span class="alternix-cross-sell__price">${currency(Number(variant.price) || 0)}</span>
        ${hasOptions ? `<a class="alternix-cross-sell__action" href="${esc(product.url)}">Choose options</a>` : `<button class="alternix-cross-sell__action" type="button" data-cross-sell-add="${esc(variant.id)}">Add to cart <span aria-hidden="true">+</span></button>`}
      </div>
    </article>`;
  }

  function render(host, products, label) {
    if (!host?.isConnected || !products.length) { if (host) host.hidden = true; return; }
    host.innerHTML = `<div class="alternix-cross-sell__head"><h3>${esc(label)}</h3></div><div class="alternix-cross-sell__rail">${products.map(card).join('')}</div>`;
    host.hidden = false;
  }

  async function update() {
    if (updating) return;
    const cartHost = document.querySelector('[data-alternix-cross-sell="cart"]');
    const drawerWrapper = document.getElementById('cart-notification-clone-wrapper');
    const drawerOpen = drawerWrapper?.classList.contains('active');
    const drawerList = document.getElementById('cart-notification-clone-product-list');
    if (!cartHost && !drawerOpen) return;
    updating = true;
    const current = ++generation;
    try {
      const cart = await getJSON(`${root}cart.js`);
      // Show recommendations only for baskets with 1–5 distinct products.
      // Quantity is intentionally irrelevant (e.g. 40 cans of one product).
      const distinctProducts = new Set(cart.items.map(item => item.product_id)).size;
      if (distinctProducts === 0 || distinctProducts >= 6) {
        if (cartHost) cartHost.hidden = true;
        const drawerHost = drawerList?.querySelector('[data-alternix-cross-sell="drawer"]');
        if (drawerHost) drawerHost.hidden = true;
        return;
      }
      // Cache the product lookups; only refresh the rail when basket composition changes.
      const products = await candidates(cart);
      if (current !== generation) return;
      if (cartHost) render(cartHost, products.slice(0, 3), 'Complete your stash');
      if (drawerOpen && drawerList) {
        let drawerHost = drawerList.querySelector('[data-alternix-cross-sell="drawer"]');
        if (!drawerHost) {
          drawerHost = document.createElement('div');
          drawerHost.className = 'alternix-cross-sell alternix-cross-sell--drawer';
          drawerHost.dataset.alternixCrossSell = 'drawer';
          drawerList.appendChild(drawerHost);
        }
        render(drawerHost, products.slice(0, 3), 'You might also like');
      }
    } catch (error) { console.warn('Alternix recommendations unavailable:', error); }
    finally { updating = false; }
  }

  // The cart replaces its section HTML after quantity changes; the drawer replaces its list.
  let debounceTimer;
  function schedule() { clearTimeout(debounceTimer); debounceTimer = setTimeout(update, 120); }
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-cross-sell-add]');
    if (!button || button.disabled) return;
    event.preventDefault();
    button.disabled = true;
    button.textContent = 'Adding…';
    try {
      await getJSON(`${root}cart/add.js`, {method:'POST', headers:{'Content-Type':'application/json','Accept':'application/json'}, body:JSON.stringify({id:Number(button.dataset.crossSellAdd), quantity:1})});
      const notification = document.querySelector('cart-notification');
      const fullCart = document.querySelector('cart-items.alternix-cart');
      if (fullCart || !notification?.fetchCartContents) { window.location.reload(); return; }
      await notification.fetchCartContents();
      schedule();
    } catch (error) {
      console.error('Cross-sell add failed:', error);
      button.disabled = false;
      button.textContent = 'Try again';
    }
  });

  function boot() {
    const drawerWrapper = document.getElementById('cart-notification-clone-wrapper');
    if (drawerWrapper) {
      new MutationObserver(() => { if (drawerWrapper.classList.contains('active')) schedule(); })
        .observe(drawerWrapper, {attributes:true,attributeFilter:['class']});
      const list = document.getElementById('cart-notification-clone-product-list');
      if (list) {
        new MutationObserver(records => {
          // Ignore our own recommendation card mutations.
          if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && node.classList?.contains('cart-notification-product--clone')))) schedule();
        }).observe(list, {childList:true});
      }
    }
    const main = document.getElementById('main-cart-items');
    if (main) new MutationObserver(schedule).observe(main, {childList:true});
    schedule();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, {once:true});
  else boot();
})();
